import { useMemo } from "react";
import {
  FilterItemCountsWithSelect,
  FilterOption,
  MetadataBarTypes,
  MetadataSource,
} from "types";
import { useLocation, useNavigate } from "react-router";

import MultipleSelectionsContent from "./MultipleSelectionsContent";
import FileDisplayContent from "./FileDisplayContent";
import BarDisplayContent from "./BarDisplayContent";
import { PAGE_PARAMETER } from "helpers/constants/miscellaneous";

import styles from "./style.module.scss";

interface ContentBodyProps {
  title: string;
  type: MetadataBarTypes;
  data: Array<FilterItemCountsWithSelect>;
  showNoMatchingData: boolean;
  isContentSelected: boolean;
  entityTypeFilter: FilterOption[];
  resetSection: any;
  sources?: MetadataSource[];
  showCount: boolean;
  isLoading: boolean;
  searchDisabled: boolean;
  getIsUnselectable?: (value: FilterItemCountsWithSelect) => boolean;
}

const ContentBody = ({
  title,
  type,
  data,
  sources,
  showNoMatchingData,
  isContentSelected,
  entityTypeFilter,
  resetSection,
  showCount,
  isLoading,
  searchDisabled,
  getIsUnselectable,
}: ContentBodyProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const sortedData = ["labels", "issue-types"].includes(type)
    ? data
    : data.slice().sort((a: any, b: any) => b.count - a.count);

  const updateQueryParameter = (
    options: FilterOption[],
    parameter: string,
    field: keyof FilterOption = "text",
  ) => {
    const result = options
      .filter((option) => option.selected === true)
      .map((option) => option[field] as string);

    if (result.length > 0) {
      searchParams.set(parameter, JSON.stringify(result));
    } else {
      searchParams.delete(parameter);
    }
    searchParams.set(PAGE_PARAMETER, "1");
    navigate({ search: `?${searchParams.toString()}` });
  };

  const renderContentForType = (type: MetadataBarTypes) => {
    if (data.length === 0) {
      return (
        <div className={styles.noMatchcontainer}>
          Currently no matching {title.toLowerCase()}.
        </div>
      );
    }
    switch (type) {
      case "file-display":
        return (
          <FileDisplayContent
            data={sortedData}
            isLoading={isLoading}
            searchDisabled={searchDisabled}
            showNoMatchingData={showNoMatchingData}
            isContentSelected={isContentSelected}
          />
        );
      case "labels":
        return (
          <BarDisplayContent
            type={type}
            data={sortedData}
            showCount={showCount}
            isLoading={isLoading}
            searchDisabled={searchDisabled}
            isContentSelected={isContentSelected}
            showNoMatchingData={showNoMatchingData}
          />
        );
      case "user-tags":
        return (
          <BarDisplayContent
            type={type}
            data={sortedData}
            showCount={showCount}
            isLoading={isLoading}
            searchDisabled={searchDisabled}
            isContentSelected={isContentSelected}
            showNoMatchingData={showNoMatchingData}
          />
        );
      case "issue-types":
        return (
          <BarDisplayContent
            type={type}
            data={sortedData}
            showCount={showCount}
            isLoading={isLoading}
            searchDisabled={searchDisabled}
            isContentSelected={isContentSelected}
            showNoMatchingData={showNoMatchingData}
          />
        );

      case "sources":
      case "models":
        return (
          <MultipleSelectionsContent
            data={sortedData}
            isLoading={isLoading}
            resetSection={resetSection}
            searchDisabled={searchDisabled}
            entityTypeFilter={entityTypeFilter}
            getIsUnselectable={getIsUnselectable}
            isContentSelected={isContentSelected}
            showNoMatchingData={showNoMatchingData}
            updateQueryParameter={updateQueryParameter}
            trackMetadataFilterChange={() => {}}
            contentType={type}
            sources={sources!}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div
      className={styles.contentBody}
      style={isContentSelected ? { height: "calc(100% - 40px)" } : undefined}
    >
      {renderContentForType(type)}
    </div>
  );
};

export default ContentBody;
